import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

import { type KnackDistribution } from '@/types/schema/KnackDistribution';
import { useApplicationQuery } from '@/hooks/api/queries/useApplicationQuery';
import { searchParamsOnAppLoad } from '@/utils/iframe';

type EmbedContextState = {
  embed: KnackDistribution | null | undefined;
  isEmbedDeleted: boolean;
} | null;

const EmbedContext = createContext<EmbedContextState>(null);

export function EmbedContextProvider({ children }: { children: ReactNode }) {
  const { data: application } = useApplicationQuery();

  const embed = useMemo<KnackDistribution | null | undefined>(() => {
    const embedKey = searchParamsOnAppLoad.get('embedKey');
    if (embedKey) {
      return (
        application?.settings.distributions.find(
          (distribution: KnackDistribution) => distribution.key === embedKey
        ) || null
      );
    }
    return null;
  }, [application]);

  const isEmbedDeleted = useMemo(() => {
    const embedKey = searchParamsOnAppLoad.get('embedKey');
    return !!embedKey && !embed;
  }, [embed]);

  useEffect(() => {
    if (embed?.design.body?.sizing === 'dynamic') {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://cdn.knack.com/assets/js/open-iframe-resizer.min.js';
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
    return () => {};
  }, [embed]);

  const contextValue = useMemo(
    () => ({
      embed,
      isEmbedDeleted
    }),
    [embed, isEmbedDeleted]
  );

  return <EmbedContext.Provider value={contextValue}>{children}</EmbedContext.Provider>;
}

export const useEmbedContext = () => {
  const context = useContext(EmbedContext);
  if (!context) {
    throw new Error('useEmbedContext must be used within a EmbedContextProvider');
  }
  return context;
};
